// Libs
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { ContextMenu, ContextMenuTrigger } from '../../../node_module_clones/react-contextmenu';
import { createStructuredSelector } from 'reselect';
import classNames from 'classnames';

// Utils
import { getElementId } from '../../../common/elements/utils/elementPropertyUtils';
import { isVideoFile } from '../../element/file/utils/fileGetMediaPlayerType';

// Components
import ElementContextMenuItemsContainer from './ElementContextMenuItemsContainer';
import CanvasContextMenuItemsContainer from './CanvasContextMenuItemsContainer';
import FileInput from '../../components/tools/files/FileInput';
import BackgroundColorPicker from './BackgroundColorPicker';
import ElementColorPicker from '../../components/colors/ElementColorPicker';

// Selectors
import { getCurrentBoardIdFromState } from '../../reducers/currentBoardId/currentBoardIdSelector';
import { getSelectedElements } from '../../element/selection/selectedElementsSelector';
import { getCurrentFocus } from '../../reducers/focusSelector';
import { isCurrentlyEditing } from '../../element/selection/currentlyEditingSelector';
import { isDraggingSelector } from '../../reducers/draggingSelector';
import { canvasZoomScaleSelector, canvasZoomTranslationPxSelector } from '../../canvas/store/canvasZoomSelector';

// Actions
import { uploadImage } from '../../element/attachments/attachmentActions';

// Styles
import './ContextMenu.scss';
import { OpenColorPickerEvent } from '../toolbar/components/selectionTools/colorTools/OpenColorPickerEvent';

const ContextMenuSwitcher = ({
    gridSize,
    renderMenu,
    location,
    selectedElements,
    rootBoardId,
    isEditable,
    currentBoardId,
    openFileSelect,
    registerMenuItem,
    triggerMenuItemOnClick,
    openBackgroundColorPicker,
    openElementColorPicker,
}) => {
    if (!renderMenu) return null;

    return selectedElements.size ? (
        <ElementContextMenuItemsContainer
            gridSize={gridSize}
            registerMenuItem={registerMenuItem}
            isEditable={isEditable}
            currentBoardId={currentBoardId}
            openFileSelect={openFileSelect}
            triggerMenuItemOnClick={triggerMenuItemOnClick}
            openElementColorPicker={openElementColorPicker}
        />
    ) : (
        <CanvasContextMenuItemsContainer
            registerMenuItem={registerMenuItem}
            location={location}
            rootBoardId={rootBoardId}
            openBackgroundColorPicker={openBackgroundColorPicker}
            triggerMenuItemOnClick={triggerMenuItemOnClick}
        />
    );
};

ContextMenuSwitcher.propTypes = {
    gridSize: PropTypes.number,
    renderMenu: PropTypes.bool,
    location: PropTypes.object,
    selectedElements: PropTypes.object,
    rootBoardId: PropTypes.string,
    isEditable: PropTypes.bool,
    currentBoardId: PropTypes.string,
    openFileSelect: PropTypes.func,
    registerMenuItem: PropTypes.func,
    triggerMenuItemOnClick: PropTypes.func,
    openBackgroundColorPicker: PropTypes.func,
    openElementColorPicker: PropTypes.func,
};

const getOnboardingDisableContextMenu = (state) =>
    state.getIn(['app', 'onboarding', 'currentStep', 'disable', 'contextMenu']);

const getCurrentBoardId = (state, props) => props.currentBoardId || getCurrentBoardIdFromState(state);

const mapStateToProps = createStructuredSelector({
    selectedElements: getSelectedElements,
    isEditing: isCurrentlyEditing,
    onboardingDisableContextMenu: getOnboardingDisableContextMenu,
    currentBoardId: getCurrentBoardId,
    isDragging: isDraggingSelector,
    zoomScale: canvasZoomScaleSelector,
    zoomTranslationPx: canvasZoomTranslationPxSelector,
});

const mapDispatchToProps = (dispatch) => ({
    // Need to call action to get currentFocus because react-contextmenu renders
    // before the props update therefore getting the old current focus if mapStateToProps
    dispatchGetCurrentFocus: () => dispatch((_, getState) => getCurrentFocus(getState())),
    dispatchUploadImage: (args) => dispatch(uploadImage(args)),
});

@connect(mapStateToProps, mapDispatchToProps)
class ContextMenuManager extends React.Component {
    constructor(props) {
        super(props);
        this.refsMap = {};
        this.state = {
            renderMenu: false,
            menuVisible: false,
            location: {},
        };

        this.fileInputRef = React.createRef();
        this.elementColorOpener = new EventTarget();
        this.backgroundColorOpener = new EventTarget();

        this.contextMenuTriggerRef = React.createRef();
        this.contextMenuRef = React.createRef();
    }

    componentDidMount() {
        if (!this.menuItems) return;
        this.menuItems.addEventListener('contextmenu', this.preventContextMenu);
    }

    componentWillUnmount() {
        if (!this.menuItems) return;
        this.menuItems.removeEventListener('contextmenu', this.preventContextMenu);
    }

    componentDidUpdate(prevProps) {
        if (!prevProps.isDragging && this.props.isDragging) {
            this.contextMenuRef.current && this.contextMenuRef.current.handleHide(new Event('fake-event'));
        }
    }

    onShowMenu = (event) => {
        const { getLocation } = this.props;

        const location = getLocation(event, this.props);

        if (!location) return;

        // First set the location that the menu should render in
        this.setState({ location: { ...location } });

        // Then render the menu
        requestAnimationFrame(() => {
            this.setState({ renderMenu: true });

            // Then show the menu.
            // This allows the menu's position to be corrected by the context menu library when the menu is near
            // the bottom of the page before it's shown to the user
            requestAnimationFrame(() => this.setState({ menuVisible: true }));
        });
    };

    onHideMenu = () => {
        this.setState({ renderMenu: false, menuVisible: false });
    };

    preventContextMenu = (event) => {
        if (!this.props.isEditable) return;
        event.preventDefault();
        event.stopPropagation();
    };

    registerMenuItem = (refName, component) => {
        this.refsMap[refName] = component;
    };

    triggerMenuItemOnClick = (refName) => (event) => {
        if (event.button !== 2 || !this.refsMap[refName] || !this.refsMap[refName].firstChild) return;

        this.refsMap[refName].firstChild.click();
    };

    openFileSelect = () => {
        this.fileInputRef.current && this.fileInputRef.current.click();
    };

    openElementColorPicker = () => {
        this.elementColorOpener.dispatchEvent(new OpenColorPickerEvent());
    };

    openBackgroundColorPicker = () => {
        this.backgroundColorOpener.dispatchEvent(new OpenColorPickerEvent());
    };

    replaceImage = (file, transactionId) => {
        const { selectedElements, dispatchUploadImage } = this.props;
        selectedElements.forEach((el) => {
            const id = getElementId(el);
            const imageType = el.get('elementType');
            dispatchUploadImage({
                id,
                file,
                imageType,
                transactionId,
                options: { onlyReplaceImage: isVideoFile(el) },
            });
        });
    };

    render() {
        const {
            currentBoard,
            menuId,
            children,
            className,
            isEditing,
            isEditable,
            onboardingDisableContextMenu,
            blockTemplateInteraction,
            selectedElements,
        } = this.props;

        const { renderMenu, menuVisible, location } = this.state;

        const disableContextMenu = !isEditable || onboardingDisableContextMenu || blockTemplateInteraction || isEditing;

        return (
            <div className={classNames('ContextMenuManager', className)}>
                <ContextMenuTrigger
                    ref={this.contextMenuTriggerRef}
                    id={menuId}
                    disable={disableContextMenu}
                    holdToDisplay={-1}
                >
                    {children}
                </ContextMenuTrigger>

                <ContextMenu
                    id={menuId}
                    ref={this.contextMenuRef}
                    className={classNames('ContextMenu', { hide: !menuVisible || !renderMenu })}
                    onShow={this.onShowMenu}
                    onHide={this.onHideMenu}
                >
                    <FileInput ref={this.fileInputRef} id="context-menu" uploadFile={this.replaceImage} />
                    <BackgroundColorPicker currentBoard={currentBoard} openerEventTarget={this.backgroundColorOpener} />
                    {selectedElements && selectedElements.size ? (
                        <ElementColorPicker elements={selectedElements} openerEventTarget={this.elementColorOpener} />
                    ) : null}

                    <div
                        ref={(c) => {
                            this.menuItems = c;
                        }}
                    >
                        <ContextMenuSwitcher
                            {...this.props}
                            renderMenu={renderMenu}
                            location={location}
                            openElementColorPicker={this.openElementColorPicker}
                            openBackgroundColorPicker={this.openBackgroundColorPicker}
                            openFileSelect={this.openFileSelect}
                            registerMenuItem={this.registerMenuItem}
                            triggerMenuItemOnClick={this.triggerMenuItemOnClick}
                        />
                    </div>
                </ContextMenu>
            </div>
        );
    }
}

ContextMenuManager.propTypes = {
    menuId: PropTypes.string.isRequired,
    children: PropTypes.node,
    className: PropTypes.string,
    gridSize: PropTypes.number,
    currentBoardId: PropTypes.string,
    isEditing: PropTypes.bool,
    isDragging: PropTypes.bool,
    onboardingDisableContextMenu: PropTypes.bool,
    blockTemplateInteraction: PropTypes.bool,
    selectedElements: PropTypes.object,
    rootBoardId: PropTypes.string,
    isEditable: PropTypes.bool,
    dispatchGetCurrentFocus: PropTypes.func,
    dispatchUploadImage: PropTypes.func,
    getLocation: PropTypes.func.isRequired,
    currentBoard: PropTypes.object,
};

export default ContextMenuManager;
